// Yolcular, gruplar, Excel içe aktarma (C21–C25): Zod girdileri ve yanıt tipleri (API ve konsol aynı tipi kullanır).
// Kişisel veri en azda tutulur: listede telefon maskeli, çocuk verisi (veli, teslim alacak kişi) yalnız Okul modülünde.
import { z } from 'zod';
import { PASSENGER_KINDS, type BoardingMethod, type BoardingType, type Direction, type PassengerKind } from '../enums';

// ───────── sabitler ─────────

export const ROSTER_KIND_LABELS: Record<PassengerKind, string> = {
  EMPLOYEE: 'Çalışan',
  STUDENT: 'Öğrenci',
  GENERAL: 'Genel',
};

/** Uygulama durumu: kullanıyor (giriş yaptı) · davet gönderildi, bekliyor · henüz davet edilmedi. */
export const ROSTER_APP_STATUSES = ['USING', 'INVITED', 'NONE'] as const;
export type RosterAppStatus = (typeof ROSTER_APP_STATUSES)[number];
export const ROSTER_APP_STATUS_LABELS: Record<RosterAppStatus, string> = {
  USING: 'Uygulamayı kullanıyor',
  INVITED: 'Davet bekliyor',
  NONE: 'Davet edilmedi',
};

/** Bugünkü durum (son biniş olayı > devamsızlık > haftalık plan > sefer var mı). */
export const ROSTER_TODAY_STATES = [
  'BOARDED',
  'ALIGHTED',
  'DELIVERED',
  'NO_SHOW',
  'ABSENT',
  'ABSENT_MORNING',
  'ABSENT_EVENING',
  'EXPECTED',
  'NO_TRIP',
  'UNASSIGNED',
] as const;
export type RosterTodayState = (typeof ROSTER_TODAY_STATES)[number];
export const ROSTER_TODAY_LABELS: Record<RosterTodayState, string> = {
  BOARDED: 'Bindi',
  ALIGHTED: 'İndi',
  DELIVERED: 'Teslim edildi',
  NO_SHOW: 'Binmedi',
  ABSENT: 'Bugün binmeyecek',
  ABSENT_MORNING: 'Sabah binmeyecek',
  ABSENT_EVENING: 'Akşam binmeyecek',
  EXPECTED: 'Bekleniyor',
  NO_TRIP: 'Bugün sefer yok',
  UNASSIGNED: 'Durak atanmadı',
};

export const ROSTER_GROUP_TYPES = ['DEPARTMENT', 'CLASS', 'SHIFT', 'BUILDING', 'OTHER'] as const;
export type RosterGroupType = (typeof ROSTER_GROUP_TYPES)[number];
export const ROSTER_GROUP_TYPE_LABELS: Record<RosterGroupType, string> = {
  DEPARTMENT: 'Departman',
  CLASS: 'Sınıf',
  SHIFT: 'Vardiya',
  BUILDING: 'Bina',
  OTHER: 'Diğer',
};

export const GUARDIAN_RELATIONS = ['MOTHER', 'FATHER', 'GUARDIAN'] as const;
export type GuardianRelation = (typeof GUARDIAN_RELATIONS)[number];
export const GUARDIAN_RELATION_LABELS: Record<GuardianRelation, string> = {
  MOTHER: 'Anne',
  FATHER: 'Baba',
  GUARDIAN: 'Vasi',
};

export const PICKUP_RELATION_LABELS: Record<string, string> = {
  ANNE: 'Anne',
  BABA: 'Baba',
  ANNEANNE: 'Anneanne',
  BABAANNE: 'Babaanne',
  DEDE: 'Dede',
  BAKICI: 'Bakıcı',
  DIGER: 'Diğer',
};

export const KVKK_REQUEST_LABELS: Record<string, string> = {
  EXPORT: 'Verileri dışa aktar',
  DELETE: 'Hesabı sil',
  ANONYMIZE: 'Anonimleştir',
};
export const KVKK_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Bekliyor',
  PROCESSING: 'İşleniyor',
  DONE: 'Tamamlandı',
  REJECTED: 'Reddedildi',
};

// ───────── liste (C21) ─────────

/** Kurum paneli liste kesimleri: çalışanlar (öğrenci dışı) ve öğrenciler. */
export const ROSTER_SEGMENTS = ['EMPLOYEES', 'STUDENTS'] as const;
export type RosterSegment = (typeof ROSTER_SEGMENTS)[number];

export interface RosterStopRef {
  stopId: string;
  stopName: string;
  routeId: string;
  routeCode: string;
  routeName: string;
  walkingM: number | null;
}

export interface RosterPassengerRow {
  id: string;
  firstName: string;
  lastName: string;
  kind: PassengerKind;
  institutionId: string;
  institution: string;
  groupId: string | null;
  group: string | null;
  className: string | null;
  externalId: string | null;
  /** KVKK: listede yalnız maskeli */
  phoneMasked: string | null;
  status: string;
  morning: RosterStopRef | null;
  evening: RosterStopRef | null;
  app: RosterAppStatus;
  invitedAt: string | null;
  /** yalnız NFC modülü açıkken */
  nfcCardNo: string | null;
  today: RosterTodayState;
}

export interface RosterSummary {
  total: number;
  using: number;
  unassigned: number;
  absentToday: number;
}

export interface RosterPage {
  items: RosterPassengerRow[];
  total: number;
  page: number;
  pageSize: number;
  summary: RosterSummary;
}

export const RosterListQuery = z.object({
  q: z.string().trim().max(80).optional(),
  institutionId: z.string().max(64).optional(),
  /** grup kimliği ya da 'none' (grubu olmayanlar) */
  groupId: z.string().max(64).optional(),
  /** rota kimliği ya da 'none' (bu firmanın hiçbir durağına atanmamış) */
  routeId: z.string().max(64).optional(),
  kind: z.enum(PASSENGER_KINDS).optional(),
  /**
   * Kurum paneli kesimi (B05 / B07): liste ve özet bu kesime göre. EMPLOYEES = öğrenci dışı yolcular,
   * STUDENTS = öğrenciler (Okul modülü kapalıysa 404).
   */
  segment: z.enum(ROSTER_SEGMENTS).optional(),
  app: z.enum(['USING', 'PENDING']).optional(),
  status: z.enum(['active', 'inactive', 'all']).default('active'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type RosterListQuery = z.infer<typeof RosterListQuery>;

export interface RosterOptions {
  institutions: { id: string; name: string; kind: string | null }[];
  groups: { id: string; institutionId: string; name: string; type: RosterGroupType }[];
  routes: { id: string; code: string; name: string; institutionId: string }[];
  flags: { school: boolean; nfc: boolean };
}

// ───────── detay (C22) ─────────

export interface RosterAssignment extends RosterStopRef {
  direction: Direction;
  routeVersion: number;
}

export interface RosterBoarding {
  id: string;
  at: string;
  type: BoardingType;
  method: BoardingMethod;
  tripId: string;
  tripDate: string;
  direction: Direction;
  routeCode: string;
  stopName: string | null;
}

export interface RosterAbsence {
  id: string;
  date: string;
  scope: 'MORNING' | 'EVENING' | 'ALL_DAY';
  reason: string | null;
  source: string;
}

export interface RosterGuardian {
  id: string;
  name: string;
  relation: string;
  isPrimary: boolean;
  phoneMasked: string;
  usingApp: boolean;
}

export interface RosterPickupPerson {
  id: string;
  name: string;
  relation: string;
  phoneMasked: string;
  authorized: boolean;
  revokedAt: string | null;
  createdAt: string;
}

export interface RosterNfcCard {
  id: string;
  cardNo: string;
  status: string;
  issuedAt: string;
}

export interface RosterKvkkRequest {
  id: string;
  type: string;
  status: string;
  reason: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface RosterComplaint {
  id: string;
  number: string;
  category: string;
  status: string;
  createdAt: string;
}

export interface RosterPassengerDetail {
  id: string;
  firstName: string;
  lastName: string;
  kind: PassengerKind;
  status: string;
  institution: { id: string; name: string; kind: string | null };
  group: { id: string; name: string; type: string } | null;
  className: string | null;
  externalId: string | null;
  phoneMasked: string | null;
  homeAddress: string | null;
  hasHomeLocation: boolean;
  createdAt: string;
  app: RosterAppStatus;
  invitedAt: string | null;
  lastSeenAt: string | null;
  today: RosterTodayState;
  assignments: RosterAssignment[];
  /** 1 = Pazartesi … 7 = Pazar; kayıt yoksa iki yön de açık */
  weekly: { dayOfWeek: number; morning: boolean; evening: boolean }[];
  absences: RosterAbsence[];
  boardings: RosterBoarding[];
  complaints: RosterComplaint[];
  /** yalnız Okul modülü açıkken ve öğrencide */
  guardians: RosterGuardian[] | null;
  pickupPersons: RosterPickupPerson[] | null;
  /** yalnız NFC modülü açıkken */
  nfcCards: RosterNfcCard[] | null;
  kvkk: RosterKvkkRequest[];
}

// ───────── ekle / düzenle (C23) ─────────

const Name = z.string().trim().min(1, 'Zorunlu alan').max(60);
const Opt = (max: number) => z.string().trim().max(max).nullable().optional();

export const RosterGuardianInput = z.object({
  firstName: Name,
  lastName: Name,
  phone: z.string().trim().min(10).max(20),
  relation: z.enum(GUARDIAN_RELATIONS),
  isPrimary: z.boolean().default(false),
});
export type RosterGuardianInput = z.infer<typeof RosterGuardianInput>;

export const RosterPassengerInput = z.object({
  institutionId: z.string().min(1).max(64),
  kind: z.enum(PASSENGER_KINDS),
  firstName: Name,
  lastName: Name,
  /** serbest giriş; sunucu E.164'e çevirir (+905xxxxxxxxx) */
  phone: Opt(20),
  externalId: Opt(40),
  groupId: z.string().max(64).nullable().optional(),
  className: Opt(20),
  homeAddress: Opt(200),
  homeLat: z.number().min(-90).max(90).nullable().optional(),
  homeLng: z.number().min(-180).max(180).nullable().optional(),
  morningStopId: z.string().max(64).nullable().optional(),
  eveningStopId: z.string().max(64).nullable().optional(),
  /** yalnız öğrenci + Okul modülü; düzenlemede yalnız yeni veli eklenir */
  guardians: z.array(RosterGuardianInput).max(4).optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
});
export type RosterPassengerInput = z.infer<typeof RosterPassengerInput>;

export const RosterPassengerUpdate = RosterPassengerInput.omit({ institutionId: true }).partial();
export type RosterPassengerUpdate = z.infer<typeof RosterPassengerUpdate>;

/** Düzenleme formu için tam değerler (telefon açık; yalnız düzenleme yetkisiyle). */
export interface RosterPassengerForm {
  id: string;
  institutionId: string;
  kind: PassengerKind;
  firstName: string;
  lastName: string;
  phone: string | null;
  externalId: string | null;
  groupId: string | null;
  className: string | null;
  homeAddress: string | null;
  homeLat: number | null;
  homeLng: number | null;
  morningStopId: string | null;
  eveningStopId: string | null;
  status: string;
  guardians: RosterGuardian[] | null;
}

export const RosterStopOptionsQuery = z.object({
  institutionId: z.string().min(1).max(64),
  lat: z.coerce.number().min(-90).max(90).optional(),
  lng: z.coerce.number().min(-180).max(180).optional(),
  routeId: z.string().max(64).optional(),
});

export interface RosterStopOption {
  stopId: string;
  name: string;
  order: number;
  routeId: string;
  routeCode: string;
  routeName: string;
  routeVersion: number;
  /** ev konumuna kuş uçuşu mesafe (konum yoksa null) */
  distanceM: number | null;
  /** tahmini yürüme (kuş uçuşu × 1,25) */
  walkingM: number | null;
  assigned: number;
}

// ───────── toplu işlemler ─────────

export const RosterInviteInput = z.object({ ids: z.array(z.string().max(64)).min(1).max(500) });
export interface RosterInviteResult {
  sent: number;
  skipped: number;
  using: number;
  noPhone: number;
}

export const RosterKvkkInput = z.object({
  type: z.enum(['EXPORT', 'ANONYMIZE']),
  reason: z.string().trim().max(300).optional(),
});

// ───────── gruplar (C25) ─────────

export interface RosterGroup {
  id: string;
  institutionId: string;
  institution: string;
  type: RosterGroupType;
  name: string;
  members: number;
}

export const RosterGroupQuery = z.object({
  institutionId: z.string().max(64).optional(),
  type: z.enum(ROSTER_GROUP_TYPES).optional(),
  q: z.string().trim().max(60).optional(),
});

export const RosterGroupInput = z.object({
  institutionId: z.string().min(1).max(64),
  type: z.enum(ROSTER_GROUP_TYPES),
  name: z.string().trim().min(1, 'Grup adı girin').max(60),
});
export const RosterGroupUpdate = RosterGroupInput.omit({ institutionId: true }).partial();

export const RosterGroupMembersInput = z.object({
  add: z.array(z.string().max(64)).max(1000).default([]),
  remove: z.array(z.string().max(64)).max(1000).default([]),
});
export interface RosterGroupMembersResult {
  added: number;
  removed: number;
  /** başka kuruma ait ya da bulunamayan */
  skipped: number;
}

// ───────── Excel içe aktarma (C24) ─────────

export const ROSTER_IMPORT_MAX_ROWS = 5000;

export const ROSTER_IMPORT_FIELDS = [
  'fullName',
  'firstName',
  'lastName',
  'phone',
  'externalId',
  'kind',
  'institution',
  'group',
  'className',
  'address',
  'route',
  'stop',
  'guardianName',
  'guardianPhone',
] as const;
export type RosterImportField = (typeof ROSTER_IMPORT_FIELDS)[number];

export const ROSTER_IMPORT_FIELD_LABELS: Record<RosterImportField, string> = {
  fullName: 'Ad soyad',
  firstName: 'Ad',
  lastName: 'Soyad',
  phone: 'Telefon',
  externalId: 'Personel / öğrenci no',
  kind: 'Yolcu türü',
  institution: 'Kurum',
  group: 'Grup',
  className: 'Sınıf',
  address: 'Adres',
  route: 'Rota',
  stop: 'Durak',
  guardianName: 'Veli adı soyadı',
  guardianPhone: 'Veli telefonu',
};

/** Okul modülüne bağlı alanlar (kapalıyken eşleme listesinde görünmez). */
export const ROSTER_IMPORT_SCHOOL_FIELDS: RosterImportField[] = ['className', 'guardianName', 'guardianPhone'];

/** Türkçe başlık eş anlamlıları (küçük harf, noktalama yok). İlk eşleşen alan kazanır. */
export const ROSTER_IMPORT_ALIASES: Record<RosterImportField, string[]> = {
  fullName: [
    'ad soyad',
    'adı soyadı',
    'adi soyadi',
    'ad soyadı',
    'isim soyisim',
    'ad ve soyad',
    'yolcu',
    'yolcu adı',
    'personel adı',
    'öğrenci adı',
    'name',
  ],
  firstName: ['ad', 'adı', 'adi', 'isim', 'first name', 'firstname'],
  lastName: ['soyad', 'soyadı', 'soyadi', 'soyisim', 'last name', 'lastname', 'surname'],
  phone: [
    'telefon',
    'tel',
    'cep',
    'cep telefonu',
    'cep tel',
    'gsm',
    'telefon no',
    'telefon numarası',
    'phone',
    'mobile',
  ],
  externalId: [
    'personel no',
    'personel numarası',
    'sicil',
    'sicil no',
    'öğrenci no',
    'öğrenci numarası',
    'okul no',
    'numara',
    'no',
    'id',
  ],
  kind: ['tür', 'yolcu türü', 'tip', 'type'],
  institution: ['kurum', 'şirket', 'firma', 'okul', 'kurum adı'],
  group: ['grup', 'departman', 'bölüm', 'birim', 'vardiya', 'bina', 'group', 'department'],
  className: ['sınıf', 'sinif', 'şube', 'class'],
  address: ['adres', 'ev adresi', 'ikamet adresi', 'açık adres', 'address', 'semt', 'mahalle'],
  route: ['rota', 'hat', 'güzergah', 'güzergâh', 'rota kodu', 'servis', 'route'],
  stop: ['durak', 'biniş durağı', 'durak adı', 'stop'],
  guardianName: ['veli', 'veli adı', 'veli adı soyadı', 'veli ad soyad'],
  guardianPhone: ['veli telefonu', 'veli tel', 'veli cep', 'veli gsm', 'veli telefon'],
};

export function normalizeHeader(h: string): string {
  return h
    .toLocaleLowerCase('tr-TR')
    .replace(/[_\-./:;()]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Başlıkları alanlara otomatik eşler: önce tam eşleşme, sonra "içerir"; her kolon en fazla bir alana. */
export function matchRosterColumns(
  headers: string[],
  fields: readonly RosterImportField[] = ROSTER_IMPORT_FIELDS,
): Partial<Record<RosterImportField, string>> {
  const out: Partial<Record<RosterImportField, string>> = {};
  const used = new Set<string>();
  const norm = headers.map((h) => ({ raw: h, n: normalizeHeader(h) }));
  // tam eşleşme: uzun eş anlamlılar önce (veli telefonu → telefon'dan önce)
  const pairs = fields
    .flatMap((f) => ROSTER_IMPORT_ALIASES[f].map((a) => ({ f, a })))
    .sort((x, y) => y.a.length - x.a.length);
  for (const { f, a } of pairs) {
    if (out[f]) continue;
    const hit = norm.find((h) => !used.has(h.raw) && h.n === a);
    if (hit) {
      out[f] = hit.raw;
      used.add(hit.raw);
    }
  }
  for (const { f, a } of pairs) {
    if (out[f] || a.length < 4) continue;
    const hit = norm.find((h) => !used.has(h.raw) && h.n.includes(a));
    if (hit) {
      out[f] = hit.raw;
      used.add(hit.raw);
    }
  }
  // ad soyad tek kolonda ise ayrı ad/soyad eşlemesi gereksiz
  if (out.fullName && out.firstName && out.lastName) delete out.fullName;
  return out;
}

const Cell = z.string().max(300);
export const RosterImportRow = z.partialRecord(z.enum(ROSTER_IMPORT_FIELDS), Cell);
export type RosterImportRow = z.infer<typeof RosterImportRow>;

export const RosterImportCreateInput = z.object({
  institutionId: z.string().min(1).max(64),
  fileName: z.string().trim().min(1).max(200),
  columns: z.array(z.string().max(120)).max(80),
  mapping: z.partialRecord(z.enum(ROSTER_IMPORT_FIELDS), z.string().max(120)),
  rows: z
    .array(RosterImportRow)
    .min(1, 'Dosyada satır yok.')
    .max(
      ROSTER_IMPORT_MAX_ROWS,
      `Bir içe aktarmada en fazla ${ROSTER_IMPORT_MAX_ROWS.toLocaleString('tr-TR')} satır olabilir.`,
    ),
});
export type RosterImportCreateInput = z.infer<typeof RosterImportCreateInput>;

/** Doğrulama ekranında satır düzeltme (satır sırası 0'dan). */
export const RosterImportFixInput = z.object({
  fixes: z
    .array(z.object({ index: z.number().int().min(0).max(ROSTER_IMPORT_MAX_ROWS), data: RosterImportRow }))
    .min(1)
    .max(500),
});

export const RosterImportRunInput = z.object({
  /** aynı personel no / telefonla kayıtlı yolcu varsa güncelle (kapalıysa atla) */
  updateExisting: z.boolean().default(true),
  /** kullanıcının atladığı satırlar */
  skip: z.array(z.number().int().min(0)).max(ROSTER_IMPORT_MAX_ROWS).default([]),
});

export type RosterImportAction = 'CREATE' | 'UPDATE' | 'ERROR';

export interface RosterImportIssue {
  field: RosterImportField | null;
  level: 'error' | 'warning';
  message: string;
}

export interface RosterImportRowResult {
  index: number;
  /** dosyadaki satır numarası (başlık 1. satır) */
  line: number;
  name: string;
  data: RosterImportRow;
  action: RosterImportAction;
  issues: RosterImportIssue[];
}

export const ROSTER_IMPORT_STATUS_LABELS: Record<string, string> = {
  UPLOADED: 'Yüklendi',
  MAPPED: 'Eşlendi',
  VALIDATED: 'Doğrulandı',
  IMPORTING: 'İçe aktarılıyor',
  DONE: 'Tamamlandı',
  FAILED: 'Başarısız',
};

export interface RosterImportJob {
  id: string;
  institutionId: string;
  institution: string;
  fileName: string;
  status: string;
  total: number;
  valid: number;
  invalid: number;
  imported: number;
  /** içe aktarma sırasında işlenen satır */
  processed: number;
  counts: { create: number; update: number; error: number; warning: number };
  result: { created: number; updated: number; skipped: number; failed: number } | null;
  createdAt: string;
  createdBy: string | null;
}

export interface RosterImportDetail extends RosterImportJob {
  mapping: Partial<Record<RosterImportField, string>>;
  /** sorunlu satırlar (hata/uyarı; en fazla 1.000) */
  problems: RosterImportRowResult[];
  /** sorunsuz satırlardan ilk 5'i (önizleme) */
  sample: RosterImportRowResult[];
}
